/**
 * Default service intervals, offered as suggestions when a rider looks at a
 * part (or a bike). Approved by Toby on 23 September 2026 from
 * docs/service-intervals-proposed.md. Nothing is ever added automatically:
 * the rider taps to add each one.
 *
 * Thresholds are SI, as stored: metres for distance, seconds for hours,
 * whole days for days.
 */
import type { ComponentCategory, IntervalBasis } from './enums'

export interface DefaultInterval {
  label: string
  basis: IntervalBasis
  threshold: number
  /** Shown with the suggestion. */
  note?: string
}

interface Rule extends DefaultInterval {
  category: ComponentCategory | 'bike'
  /** Only for these brands (case-insensitive). */
  brands?: readonly string[]
  /** Only when this spec test passes. */
  when?: (specs: Record<string, unknown>) => boolean
}

const H = 3600
const KM = 1000
const MANUAL = "Check the maker's service manual for your model."

const RULES: readonly Rule[] = [
  // Forks
  {
    category: 'fork',
    brands: ['fox'],
    label: 'Lower leg service',
    basis: 'hours',
    threshold: 125 * H,
  },
  {
    category: 'fork',
    brands: ['fox'],
    label: 'Damper and air spring service',
    basis: 'hours',
    threshold: 250 * H,
  },
  {
    category: 'fork',
    brands: ['rockshox'],
    label: 'Lower leg service',
    basis: 'hours',
    threshold: 50 * H,
  },
  {
    category: 'fork',
    brands: ['rockshox'],
    label: 'Damper and spring service',
    basis: 'hours',
    threshold: 200 * H,
  },
  {
    category: 'fork',
    label: 'Lower leg service',
    basis: 'hours',
    threshold: 50 * H,
    note: MANUAL,
  },
  {
    category: 'fork',
    label: 'Damper and spring service',
    basis: 'hours',
    threshold: 200 * H,
    note: MANUAL,
  },
  // Rear shocks
  {
    category: 'shock',
    brands: ['fox'],
    label: 'Air can service',
    basis: 'hours',
    threshold: 125 * H,
  },
  {
    category: 'shock',
    brands: ['fox'],
    label: 'Full damper service',
    basis: 'hours',
    threshold: 250 * H,
  },
  {
    category: 'shock',
    brands: ['rockshox'],
    label: 'Air can service',
    basis: 'hours',
    threshold: 50 * H,
  },
  {
    category: 'shock',
    brands: ['rockshox'],
    label: 'Damper service',
    basis: 'hours',
    threshold: 200 * H,
  },
  {
    category: 'shock',
    label: 'Air can service',
    basis: 'hours',
    threshold: 50 * H,
    note: MANUAL,
  },
  {
    category: 'shock',
    label: 'Damper service',
    basis: 'hours',
    threshold: 200 * H,
    note: MANUAL,
  },
  // Everything else
  {
    category: 'seatpost',
    label: 'Dropper service',
    basis: 'hours',
    threshold: 200 * H,
    note: 'Varies widely by brand.',
    when: (s) => s.type === 'Dropper',
  },
  {
    category: 'chain',
    label: 'Check chain wear with a gauge',
    basis: 'distance',
    threshold: 500 * KM,
    note: 'Replace at 0.5% stretch for 11- and 12-speed, 0.75% for older chains.',
  },
  {
    category: 'brake_pads',
    label: 'Check pad thickness',
    basis: 'distance',
    threshold: 300 * KM,
    note: 'Replace below about 0.5 mm of pad material.',
  },
  {
    category: 'brake',
    label: 'Bleed',
    basis: 'days',
    threshold: 365,
    note: 'Sooner if the lever feels spongy.',
    when: (s) => s.type === undefined || s.type === 'Hydraulic disc',
  },
  {
    category: 'sealant',
    label: 'Top up sealant',
    basis: 'days',
    threshold: 90,
    note: 'Sooner in hot, dry weather.',
  },
  { category: 'headset', label: 'Check, clean and grease', basis: 'days', threshold: 180 },
  { category: 'bottom_bracket', label: 'Check for play and noise', basis: 'days', threshold: 180 },
  { category: 'bike', label: 'Full service', basis: 'days', threshold: 365 },
]

/** Suggested intervals for a part, or for a whole bike (pass 'bike'). */
export function suggestedIntervals(
  target:
    | 'bike'
    | { category: ComponentCategory; brand?: string | null; specs?: Record<string, unknown> },
): DefaultInterval[] {
  const category = target === 'bike' ? 'bike' : target.category
  const brand =
    target === 'bike' ? '' : (target.brand ?? '').trim().toLowerCase().replace(/\s+/g, '')
  const specs = target === 'bike' ? {} : (target.specs ?? {})
  const forCategory = RULES.filter((r) => r.category === category && (!r.when || r.when(specs)))
  // A brand's own figures if we have them; otherwise the general ones.
  const branded = forCategory.filter((r) => r.brands?.includes(brand))
  const chosen = branded.length ? branded : forCategory.filter((r) => !r.brands)
  return chosen.map(({ label, basis, threshold, note }) => ({
    label,
    basis,
    threshold,
    ...(note ? { note } : {}),
  }))
}

/** Every default, for the seed summary and docs. */
export const DEFAULT_INTERVAL_COUNT = RULES.length
